import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { 
  Filter, X, ChevronDown, ChevronUp, MapPin, Briefcase, 
  IndianRupee, Clock, GraduationCap, Search, User
} from 'lucide-react';

const EXPERIENCE_OPTIONS = [
  'Fresher',
  '0-1 years',
  '1-2 years',
  '2-3 years',
  '3-5 years',
  '5-7 years',
  '7-10 years',
  '10-15 years',
  '15+ years',
];

const SALARY_RANGES = [
  { label: '0-3 LPA', value: '0-3' },
  { label: '3-6 LPA', value: '3-6' },
  { label: '6-10 LPA', value: '6-10' },
  { label: '10-15 LPA', value: '10-15' },
  { label: '15-25 LPA', value: '15-25' },
  { label: '25-50 LPA', value: '25-50' },
  { label: '50+ LPA', value: '50+' },
];

const NOTICE_PERIODS = [
  'Immediate',
  '15 days',
  '30 days',
  '45 days',
  '60 days',
  '90 days',
  'More than 90 days',
];

const PROFILE_STATUS = [
  { label: 'With Profile', value: 'with_profile' },
  { label: 'Without Profile', value: 'without_profile' },
];

const CandidateFilters = ({ 
  filters, 
  onFilterChange, 
  onClearFilters, 
  onSearch,
  searchQuery,
  onSearchQueryChange,
  totalCandidates 
}) => {
  const [expandedSections, setExpandedSections] = useState({
    experience: true,
    salary: true,
    noticePeriod: false,
    location: false,
    profileStatus: false,
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleCheckboxChange = (filterType, value) => {
    const currentValues = filters[filterType] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    onFilterChange({ ...filters, [filterType]: newValues });
  };

  const handleInputChange = (filterType, value) => {
    onFilterChange({ ...filters, [filterType]: value });
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.experience?.length) count += filters.experience.length;
    if (filters.salary?.length) count += filters.salary.length;
    if (filters.noticePeriod?.length) count += filters.noticePeriod.length;
    if (filters.profileStatus?.length) count += filters.profileStatus.length;
    if (filters.location) count += 1;
    if (searchQuery) count += 1;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  const FilterSection = ({ title, icon: Icon, sectionKey, children }) => (
    <div className="border-b border-gray-200 py-3">
      <button
        onClick={() => toggleSection(sectionKey)}
        className="flex items-center justify-between w-full text-left"
      >
        <span className="flex items-center gap-2 font-medium text-gray-700">
          <Icon className="w-4 h-4 text-gray-500" />
          {title}
        </span>
        {expandedSections[sectionKey] ? (
          <ChevronUp className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        )}
      </button>
      {expandedSections[sectionKey] && (
        <div className="mt-3 space-y-2">
          {children}
        </div>
      )}
    </div>
  );

  const CheckboxItem = ({ label, checked, onChange }) => (
    <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 bg-white"
      />
      <span className="text-sm text-gray-600">{label}</span>
    </label>
  );

  return (
    <Card className="bg-white border-gray-200 p-4 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-blue-600" />
          <span className="font-semibold text-gray-900">Filters</span>
          {activeFilterCount > 0 && (
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
              {activeFilterCount}
            </span>
          )}
        </div>
        {activeFilterCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8"
          >
            <X className="w-4 h-4 mr-1" />
            Clear
          </Button>
        )}
      </div>

      {/* Results Count */}
      <div className="mb-4 p-2 bg-blue-50 rounded text-sm text-blue-700">
        <strong>{totalCandidates}</strong> candidates found
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search by name, email, skills..."
            value={searchQuery}
            onChange={(e) => onSearchQueryChange(e.target.value)}
            className="pl-10 bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Location Filter */}
      <FilterSection title="Location" icon={MapPin} sectionKey="location">
        <div className="relative">
          <Input
            type="text"
            placeholder="Enter city"
            value={filters.location || ''}
            onChange={(e) => handleInputChange('location', e.target.value)}
            className="bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400 h-9 text-sm focus:border-blue-500"
          />
        </div>
      </FilterSection>

      {/* Experience Filter */}
      <FilterSection title="Experience" icon={Briefcase} sectionKey="experience">
        <div className="max-h-48 overflow-y-auto">
          {EXPERIENCE_OPTIONS.map((exp) => (
            <CheckboxItem
              key={exp}
              label={exp}
              checked={(filters.experience || []).includes(exp)}
              onChange={() => handleCheckboxChange('experience', exp)}
            />
          ))}
        </div>
      </FilterSection>

      {/* Expected Salary Filter */}
      <FilterSection title="Expected Salary" icon={IndianRupee} sectionKey="salary">
        <div className="max-h-48 overflow-y-auto">
          {SALARY_RANGES.map((range) => (
            <CheckboxItem
              key={range.value}
              label={range.label}
              checked={(filters.salary || []).includes(range.value)}
              onChange={() => handleCheckboxChange('salary', range.value)}
            />
          ))}
        </div>
      </FilterSection>

      {/* Notice Period Filter */}
      <FilterSection title="Notice Period" icon={Clock} sectionKey="noticePeriod">
        <div className="flex flex-wrap gap-2">
          {NOTICE_PERIODS.map((period) => (
            <button
              key={period}
              onClick={() => handleCheckboxChange('noticePeriod', period)}
              className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                (filters.noticePeriod || []).includes(period)
                  ? 'bg-purple-600 text-white border-purple-600'
                  : 'bg-slate-700 text-gray-300 border-slate-600 hover:border-purple-400'
              }`}
            >
              {period}
            </button>
          ))}
        </div>
      </FilterSection>

      {/* Profile Status Filter */}
      <FilterSection title="Profile Status" icon={User} sectionKey="profileStatus">
        {PROFILE_STATUS.map((status) => (
          <CheckboxItem
            key={status.value}
            label={status.label}
            checked={(filters.profileStatus || []).includes(status.value)}
            onChange={() => handleCheckboxChange('profileStatus', status.value)}
          />
        ))}
      </FilterSection>
    </Card>
  );
};

export default CandidateFilters;
